import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';

import { civicAPI } from '../api/civic';
import { useActiveStoryChapter } from '../hooks/useActiveStoryChapter';
import { normalizeScheduleWindows } from '../utils/votingScheduleFormatter';
import ElectionStoryBackdrop from './ElectionStoryBackdrop';
import ElectionStoryChapter from './ElectionStoryChapter';
import ElectionStoryProgress from './ElectionStoryProgress';
import { ElectionWeekCalendar } from './ElectionWeekCalendar';

/**
 * Mobile scrollytelling layout for busy voters.
 * 5-chapter flow: What to expect → When & how → Be ready → Your ballot → Next step
 * Reduces anxiety with calm copy, clear CTAs, and animated backdrop.
 */

// Single source of truth for the sample-ballot destination.
// Swap the return value here when a custom ballot page is ready.
function sampleBallotHref(fromAddress, electionName, electionState) {
  const query = fromAddress
    ? `!ducky sample ballot ${fromAddress} ${electionName}`
    : `!ducky sample ballot ${electionState ?? ''} site:gov`;
  return `https://duckduckgo.com/?q=${encodeURIComponent(query)}`;
}

export default function ElectionDetailStoryMobile({ election, newsData, fromAddress, fromState }) {
  const chapterIds = ['what-to-expect', 'when-and-how', 'be-ready', 'your-ballot', 'next-step'];

  const { activeChapterId, chapterStates, registerChapterRef } = useActiveStoryChapter(chapterIds);

  // Normalize schedule windows once
  const normalizedWindows = useMemo(
    () => normalizeScheduleWindows(election?.scheduleWindows),
    [election?.scheduleWindows]
  );

  // Format election day
  const electionDateObj = election ? new Date(`${election.electionDay}T00:00:00`) : null;

  const electionDateLabel = electionDateObj?.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Days until election
  const daysUntil = useCallback((dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(`${dateStr}T00:00:00`);
    return Math.round((target - today) / 86400000);
  }, []);

  const daysUntilElection = election ? daysUntil(election.electionDay) : 0;

  const { data: pollingData } = useQuery({
    queryKey: ['polling-locations', fromAddress, election?.id],
    queryFn: () => civicAPI.getPollingLocations(fromAddress, election?.id),
    enabled: !!fromAddress && !!election?.id,
    staleTime: 5 * 60 * 1000,
  });

  const pollingLocations = pollingData?.pollingLocations ?? [];

  let scheduleBlock;
  if (normalizedWindows.length > 0) {
    scheduleBlock = (
      <div className="bg-white/80 backdrop-blur-sm rounded-lg overflow-hidden border border-neutral-200">
        <ElectionWeekCalendar scheduleWindows={normalizedWindows} />
      </div>
    );
  } else if (pollingLocations.length > 0) {
    scheduleBlock = (
      <div className="space-y-2">
        {pollingLocations.map((loc, i) => (
          <div
            key={loc.id ?? i}
            className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-neutral-200"
          >
            <p className="text-sm font-semibold text-neutral-900">{loc.name}</p>
            {loc.address && <p className="text-xs text-neutral-600 mt-1">{loc.address}</p>}
            {loc.hours && <p className="text-xs text-neutral-500 mt-1">{loc.hours}</p>}
          </div>
        ))}
      </div>
    );
  } else {
    const luckyQuery = fromAddress
      ? `!ducky polling place finder ${fromAddress}`
      : `!ducky polling place finder ${election?.state ?? ''} site:gov`;
    scheduleBlock = (
      <a
        href={`https://duckduckgo.com/?q=${encodeURIComponent(luckyQuery)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-neutral-200 hover:border-primary-400 transition-colors"
      >
        <div>
          <p className="text-sm font-semibold text-neutral-900">Find your polling place</p>
          <p className="text-xs text-neutral-500 mt-0.5">
            {fromAddress ? `Search for: ${fromAddress}` : 'Search your local election authority'}
          </p>
        </div>
        <svg
          className="w-4 h-4 text-primary-600 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      </a>
    );
  }

  if (!election) {
    return null;
  }

  return (
    <div className="relative bg-white">
      {/* Animated backdrop */}
      <ElectionStoryBackdrop activeChapterId={activeChapterId} />

      {/* Progress bar */}
      <ElectionStoryProgress />

      {/* Address context banner */}
      {fromAddress && (
        <div
          className="relative z-10 px-4 py-2 flex items-center gap-2"
          style={{ background: '#ede5d4', borderBottom: '1px dashed #8a7e6b' }}
        >
          <span
            style={{ fontFamily: "'Special Elite', monospace", fontSize: '12px', color: '#504636' }}
          >
            📍 Showing info for: {fromAddress}
          </span>
        </div>
      )}

      {/* Date hero — first thing visible */}
      <div className="relative z-10 px-6 pt-10 pb-8 text-center">
        <p className="text-xs text-neutral-500 uppercase tracking-wide mb-3">Election Day</p>
        <p className="text-3xl font-bold text-neutral-900 leading-tight mb-4">
          {electionDateLabel}
        </p>
        {daysUntilElection > 0 && (
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-6xl font-bold text-primary-700 tabular-nums">
              {daysUntilElection}
            </span>
            <span className="text-xl text-neutral-500">
              day{daysUntilElection !== 1 ? 's' : ''} away
            </span>
          </div>
        )}
        {daysUntilElection === 0 && <p className="text-2xl font-bold text-red-600">Today!</p>}
        {daysUntilElection < 0 && <p className="text-base text-neutral-400">Election has passed</p>}
        <p className="text-sm text-neutral-500 mt-4 leading-snug">{election.name}</p>
      </div>

      {/* Chapter 1: What to Expect */}
      <div ref={(el) => registerChapterRef('what-to-expect', el)}>
        <ElectionStoryChapter
          id="what-to-expect"
          title="You have a right to vote"
          state={chapterStates['what-to-expect']}
        >
          <div className="space-y-6">
            <p className="text-base text-neutral-600 leading-relaxed">
              Voting is straightforward. Here&apos;s what to do before you show up.
            </p>

            {/* Vertical timeline */}
            <div className="relative pl-8">
              <div className="absolute left-3 top-2 bottom-2 w-px bg-neutral-200" />
              <div className="space-y-5">
                {[
                  {
                    label: 'Before election day',
                    text: "Confirm you're registered at your current address",
                    hint: 'Takes 2 minutes online',
                  },
                  {
                    label: null,
                    text: "Look up the ballot and decide who you'll vote for",
                    hint: 'A sample ballot search can help',
                  },
                  {
                    label: 'On election day',
                    text: 'Find your polling place and check the hours',
                    hint: null,
                  },
                  { label: null, text: 'Bring a valid photo ID', hint: 'Required in most states' },
                  { label: null, text: "Go vote — you're ready 🇺🇸", hint: null },
                ].map(({ label, text, hint }, i) => (
                  <div key={i} className="relative">
                    {label && (
                      <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2 -ml-8 pl-8">
                        {label}
                      </p>
                    )}
                    <div className="flex items-start gap-3">
                      <span className="absolute -left-5 w-4 h-4 rounded-full border-2 border-primary-600 bg-white shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-neutral-900 leading-snug">{text}</p>
                        {hint && <p className="text-xs text-neutral-400 mt-0.5">{hint}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <a
              href={`https://duckduckgo.com/?q=${encodeURIComponent(fromState ? `${fromState} voter registration site:gov` : `voter registration ${election.name} site:gov`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-4 py-3 bg-primary-600 text-white font-semibold rounded-lg text-center hover:bg-primary-700 transition-colors"
            >
              Confirm your registration
            </a>
          </div>
        </ElectionStoryChapter>
      </div>

      {/* Chapter 2: When and How You Can Vote */}
      <div ref={(el) => registerChapterRef('when-and-how', el)}>
        <ElectionStoryChapter
          id="when-and-how"
          title="When and how you can vote"
          state={chapterStates['when-and-how']}
        >
          <div className="space-y-6">
            {scheduleBlock}

            <div className="space-y-3">
              <h3 className="font-semibold text-neutral-900">Three ways to vote</h3>
              <div className="space-y-2">
                <div className="p-3 bg-white/80 backdrop-blur-sm rounded border-l-4 border-amber-400">
                  <p className="text-sm font-medium text-neutral-900">Early in person</p>
                  <p className="text-xs text-neutral-600 mt-1">
                    Before election day at designated locations
                  </p>
                </div>
                <div className="p-3 bg-white/80 backdrop-blur-sm rounded border-l-4 border-green-400">
                  <p className="text-sm font-medium text-neutral-900">Election day</p>
                  <p className="text-xs text-neutral-600 mt-1">At your registered polling place</p>
                </div>
                <div className="p-3 bg-white/80 backdrop-blur-sm rounded border-l-4 border-blue-400">
                  <p className="text-sm font-medium text-neutral-900">By mail</p>
                  <p className="text-xs text-neutral-600 mt-1">Request and return your ballot</p>
                </div>
              </div>
            </div>

            <a
              href={`https://duckduckgo.com/?q=${encodeURIComponent(fromAddress ? `polling location ${fromAddress} site:gov` : `polling location ${election.name} site:gov`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4 px-4 py-3 bg-primary-600 text-white font-semibold rounded-lg text-center hover:bg-primary-700 transition-colors"
            >
              Find your polling place
            </a>
          </div>
        </ElectionStoryChapter>
      </div>

      {/* Chapter 3: What's on Your Ballot */}
      <div ref={(el) => registerChapterRef('your-ballot', el)}>
        <ElectionStoryChapter
          id="your-ballot"
          title="What's on your ballot"
          state={chapterStates['your-ballot']}
        >
          <div className="space-y-6">
            <a
              href={sampleBallotHref(fromAddress, election.name, election?.state)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-neutral-200 hover:border-primary-400 transition-colors"
            >
              <div>
                <p className="text-sm font-semibold text-neutral-900">See what's on your ballot</p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {fromAddress ? `Search for: ${fromAddress}` : 'Search your local sample ballot'}
                </p>
              </div>
              <svg
                className="w-4 h-4 text-primary-600 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>

            {newsData?.articles && newsData.articles.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-neutral-900">Election news</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {newsData.articles.slice(0, 3).map((article) => (
                    <a
                      key={article.url}
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 bg-white/80 backdrop-blur-sm rounded border-l-4 border-neutral-300 hover:border-primary-400 transition-colors"
                    >
                      <p className="text-xs font-semibold text-neutral-900 line-clamp-2">
                        {article.title}
                      </p>
                      {article.source && (
                        <p className="text-xs text-neutral-500 mt-1">{article.source}</p>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <a
              href={sampleBallotHref(fromAddress, election.name, election?.state)}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4 px-4 py-3 bg-primary-600 text-white font-semibold rounded-lg text-center hover:bg-primary-700 transition-colors"
            >
              Preview your ballot
            </a>
          </div>
        </ElectionStoryChapter>
      </div>

      {/* Chapter 4: What You Need to Be Ready */}
      <div ref={(el) => registerChapterRef('be-ready', el)}>
        <ElectionStoryChapter
          id="be-ready"
          title="What you need to be ready"
          state={chapterStates['be-ready']}
        >
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-white/80 backdrop-blur-sm rounded">
                <svg
                  className="w-5 h-5 text-green-500 shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-neutral-900">Bring ID</p>
                  <p className="text-xs text-neutral-600 mt-1">
                    Most locations require valid photo ID
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/80 backdrop-blur-sm rounded">
                <svg
                  className="w-5 h-5 text-green-500 shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-neutral-900">Know your polling place</p>
                  <p className="text-xs text-neutral-600 mt-1">
                    Look up your location and voting hours
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/80 backdrop-blur-sm rounded">
                <svg
                  className="w-5 h-5 text-green-500 shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-neutral-900">Come with enough time</p>
                  <p className="text-xs text-neutral-600 mt-1">
                    Plan for 15–45 minutes, arrive early
                  </p>
                </div>
              </div>
            </div>

            {election?.registrationDeadline && (
              <div className="p-4 bg-white/80 backdrop-blur-sm rounded border border-amber-200">
                <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">
                  Registration deadline
                </p>
                <p className="text-base font-semibold text-neutral-900">
                  {new Date(`${election.registrationDeadline}T00:00:00`).toLocaleDateString(
                    'en-US',
                    {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    }
                  )}
                </p>
              </div>
            )}

            <a
              href={sampleBallotHref(fromAddress, election.name, election?.state)}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4 px-4 py-3 bg-primary-600 text-white font-semibold rounded-lg text-center hover:bg-primary-700 transition-colors"
            >
              See a sample ballot
            </a>
          </div>
        </ElectionStoryChapter>
      </div>

      {/* Chapter 5: Your Next Step */}
      <div ref={(el) => registerChapterRef('next-step', el)}>
        <ElectionStoryChapter
          id="next-step"
          title="Your next step"
          state={chapterStates['next-step']}
        >
          <div className="space-y-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-neutral-200">
              <p className="text-base text-neutral-700 leading-relaxed font-medium">
                You're ready. Pick a time that works for you and go vote. Your voice matters.
              </p>
            </div>

            <div className="space-y-3">
              <a
                href={`https://duckduckgo.com/?q=${encodeURIComponent(fromAddress ? `polling location ${fromAddress} site:gov` : `polling location ${election.name} site:gov`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-4 bg-primary-600 text-white font-semibold rounded-lg text-center hover:bg-primary-700 transition-colors text-lg"
              >
                Find your polling place
              </a>

              <a
                href={sampleBallotHref(fromAddress, election.name, election?.state)}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-3 bg-neutral-200 text-neutral-900 font-semibold rounded-lg text-center hover:bg-neutral-300 transition-colors"
              >
                See ballot preview
              </a>
            </div>

            {daysUntilElection > 0 && (
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-xs text-green-700 uppercase tracking-wide font-semibold">
                  Election in {daysUntilElection} day{daysUntilElection !== 1 ? 's' : ''}
                </p>
                <p className="text-2xl font-bold text-green-600 mt-2">{electionDateLabel}</p>
              </div>
            )}
          </div>
        </ElectionStoryChapter>
      </div>
    </div>
  );
}

ElectionDetailStoryMobile.propTypes = {
  election: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    electionDay: PropTypes.string,
    electionType: PropTypes.string,
    state: PropTypes.string,
    registrationDeadline: PropTypes.string,
    absenteeBallotDeadline: PropTypes.string,
    scheduleWindows: PropTypes.arrayOf(PropTypes.object),
  }),
  newsData: PropTypes.shape({
    articles: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
        source: PropTypes.string,
      })
    ),
  }),
  fromAddress: PropTypes.string,
  fromState: PropTypes.string,
};

ElectionDetailStoryMobile.defaultProps = {
  election: null,
  newsData: null,
  fromAddress: null,
  fromState: null,
};
